import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  Heading,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@eluve/components';

import { FailedTranscriptionsTab } from './FailedTranscriptionsTab';
import { GraphileJobsTab } from './GraphileJobsTab';
import { MissingNotesTab } from './MissingNotesTab';
import { NotesGeneratingTab } from './NotesGeneratingTab';
import { RecentOutputsTab } from './RecentOutputsTab';

const OnCallTabSchema = z
  .enum([
    'FAILED_TRANSCRIPTIONS',
    'MISSING_NOTES',
    'NOTES_GENERATING',
    'RECENT_OUTPUTS',
    'GRAPHILE_JOBS',
  ])
  .nullable()
  .transform((val) => val ?? 'FAILED_TRANSCRIPTIONS');

type OnCallTab = z.infer<typeof OnCallTabSchema>;

interface OnCallPageProps {
  langfuseBaseUrl?: string | null;
  langfuseProjectId?: string | null;
}

export const OnCallPage: React.FC<OnCallPageProps> = ({
  langfuseBaseUrl,
  langfuseProjectId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTab = searchParams.get('tab');
  const tab = OnCallTabSchema.parse(currentTab);

  const setTab = (newTab: OnCallTab) => {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set('tab', newTab);
    setSearchParams(urlSearchParams, { replace: true });
  };

  return (
    <Tabs value={tab} className="w-full">
      <Stack direction="horizontal" align="center" justify="between">
        <div className="my-2 py-2">
          <Heading>On-Call Dashboard</Heading>
        </div>
        <TabsList>
          <TabsTrigger
            value="FAILED_TRANSCRIPTIONS"
            onClick={() => setTab('FAILED_TRANSCRIPTIONS')}
            className="px-8"
          >
            Failed Transcriptions
          </TabsTrigger>
          <TabsTrigger
            value="MISSING_NOTES"
            onClick={() => setTab('MISSING_NOTES')}
            className="px-8"
          >
            Missing Notes
          </TabsTrigger>
          <TabsTrigger
            value="NOTES_GENERATING"
            onClick={() => setTab('NOTES_GENERATING')}
            className="px-8"
          >
            Generating
          </TabsTrigger>
          <TabsTrigger
            value="RECENT_OUTPUTS"
            onClick={() => setTab('RECENT_OUTPUTS')}
            className="px-8"
          >
            Recently Generated
          </TabsTrigger>
          <TabsTrigger
            value="GRAPHILE_JOBS"
            onClick={() => setTab('GRAPHILE_JOBS')}
            className="px-8"
          >
            Graphile Jobs
          </TabsTrigger>
        </TabsList>
      </Stack>
      <TabsContent value="FAILED_TRANSCRIPTIONS">
        <FailedTranscriptionsTab />
      </TabsContent>
      <TabsContent value="MISSING_NOTES">
        <MissingNotesTab />
      </TabsContent>
      <TabsContent value="NOTES_GENERATING">
        <NotesGeneratingTab />
      </TabsContent>
      <TabsContent value="RECENT_OUTPUTS">
        <RecentOutputsTab
          langfuseBaseUrl={langfuseBaseUrl}
          langfuseProjectId={langfuseProjectId}
        />
      </TabsContent>
      <TabsContent value="GRAPHILE_JOBS">
        <GraphileJobsTab />
      </TabsContent>
    </Tabs>
  );
};
